import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { homeLoaders, localIsoDate, type HomeLoaders } from '@/application/home/loadHome'
import { budgetRepository, inboxRepository, myRequestsRepository, weekScheduleRepository } from '@/infrastructure'
import type { BudgetUse } from '@/domain/home/Budget'
import type { WeekSchedule } from '@/domain/home/WeekSchedule'
import type { HomeBlock } from '@/domain/home/HomeBlock'
import type { InboxPage, PendingSummary } from '@/domain/inbox/Inbox'
import type { MyRequestsPage } from '@/domain/home/MyRequests'

export interface BlockState<T> {
  data: T | null
  failed: boolean
  loading: boolean
}

export type HomeBlockKey = keyof HomeLoaders

const HOME_BLOCK_KEYS: readonly HomeBlockKey[] = ['pending', 'notifications', 'requests', 'budget', 'week']

const empty = <T>(): BlockState<T> => ({ data: null, failed: false, loading: false })

export const useHomeStore = defineStore('home', () => {
  const pending = ref<BlockState<PendingSummary>>(empty())
  const notifications = ref<BlockState<InboxPage>>(empty())
  const requests = ref<BlockState<MyRequestsPage>>(empty())
  const budget = ref<BlockState<BudgetUse[]>>(empty())
  const week = ref<BlockState<WeekSchedule>>(empty())
  const states: Record<HomeBlockKey, Ref<BlockState<unknown>>> = { pending, notifications, requests, budget, week }
  const tokens: Record<HomeBlockKey, number> = { pending: 0, notifications: 0, requests: 0, budget: 0, week: 0 }
  let loaders: HomeLoaders = { pending: null, notifications: null, requests: null, budget: null, week: null }
  let loadedFor: number | null | undefined

  async function loadBlock(key: HomeBlockKey): Promise<void> {
    const loader = loaders[key]
    if (loader === null) return
    const token = ++tokens[key]
    const state = states[key]
    state.value = { ...state.value, failed: false, loading: true }
    try {
      const data = await loader()
      if (token === tokens[key]) state.value = { data, failed: false, loading: false }
    } catch {
      if (token === tokens[key]) state.value = { data: null, failed: true, loading: false }
    }
  }

  function clear(): void {
    for (const key of HOME_BLOCK_KEYS) {
      tokens[key]++
      states[key].value = empty()
    }
    loadedFor = undefined
  }

  function load(blocks: readonly HomeBlock[], userId: number | null): Promise<void[]> {
    if (userId !== loadedFor) clear()
    loadedFor = userId
    loaders = homeLoaders({ inbox: inboxRepository, requests: myRequestsRepository, budget: budgetRepository, week: weekScheduleRepository }, blocks, userId, localIsoDate(new Date()))
    return Promise.all(HOME_BLOCK_KEYS.map(loadBlock))
  }

  return { pending, notifications, requests, budget, week, load, retry: loadBlock, clear }
})
